import { useAuthStore } from "@/stores/auth";

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

export const AUTH_ERROR_MESSAGE = "Authentification requise";
const STORAGE_BASE_URL = "https://storage.efashion-paris.com";

function getUrlPathAndQuery(value: string): string {
  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      return `${url.pathname}${url.search}`;
    } catch {
      // Continuer avec la valeur brute si l'URL est mal formée.
    }
  }
  return value.startsWith("/") ? value : `/${value}`;
}

export function getApiBaseUrl(): string {
  const baseUrl = process.env.VUE_APP_API_BASE_URL;
  if (baseUrl) return baseUrl.replace(/\/$/, "");
  if (process.env.NODE_ENV === "development") return "http://localhost:3002";
  return "";
}

export function getStorageBaseUrl(): string {
  return STORAGE_BASE_URL;
}

/** Force un chemin ou une URL de fichier à être lu depuis le domaine storage. */
export function getStorageFileUrl(filePath: string): string {
  if (!filePath) return "";
  const storageBase = getStorageBaseUrl();
  let p = getUrlPathAndQuery(filePath);
  if (/^\/(docs|Boutiques)\//.test(p)) {
    p = `/Images${p}`;
  }
  p = p.replace(/^\/uploads\/groupages\//, "/Images/docs/groupages/");
  return `${storageBase}${p}`;
}

/**
 * Construit l'URL d'une image produit directement depuis le storage.
 * /uploads/products/{id}/Produits/... -> {storage}/Images/Boutiques/{id}/Produits/...
 */
export function getProductImageUrl(photoPath: string): string {
  if (!photoPath) return "";
  const storageBase = getStorageBaseUrl();
  let p = getUrlPathAndQuery(photoPath);
  p = p.replace(/^\/uploads\/products\//, "/Images/Boutiques/");
  return `${storageBase}${p}`;
}

/**
 * Construit l'URL d'une image boutique (banniere, logo-boutique, square, lookbooks...)
 * directement depuis le storage.
 * /uploads/sellers/{id}/... -> {storage}/Images/Boutiques/{id}/...
 */
export function getSellerImageUrl(photoPath: string): string {
  if (!photoPath) return "";
  // Conserver l'éventuel query (ex: ?t=timestamp pour le cache-busting)
  let query = "";
  let raw = photoPath;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      raw = u.pathname;
      query = u.search;
    } catch {
      const parts = raw.split("?");
      raw = parts[0];
      query = parts[1] ? `?${parts[1]}` : "";
    }
  } else {
    const parts = raw.split("?");
    raw = parts[0];
    query = parts[1] ? `?${parts[1]}` : "";
  }
  const storageBase = getStorageBaseUrl();
  let p = raw.startsWith("/") ? raw : `/${raw}`;
  p = p.replace(/^\/uploads\/sellers\//, "/Images/Boutiques/");
  return `${storageBase}${p}${query}`;
}

/**
 * Construit l'URL d'une preuve d'enlèvement directement depuis le storage (comme les images).
 * /uploads/preuves/{YYYY-MM}/{id}/{file} -> {storage}/Images/docs/preuves/{YYYY-MM}/{id}/{file}
 */
export function getPreuveUrl(preuvePath: string): string {
  if (!preuvePath) return "";
  const storageBase = getStorageBaseUrl();
  let p = getUrlPathAndQuery(preuvePath);
  p = p.replace(/^\/uploads\/preuves\//, "/Images/docs/preuves/");
  return `${storageBase}${p}`;
}

/**
 * Construit l'URL d'une facture vendeur directement depuis le storage (comme les images).
 * /uploads/factures/{YYYY-MM}/{id}/{file} -> {storage}/Images/docs/factures/{YYYY-MM}/{id}/{file}
 */
export function getFactureUrl(facturePath: string): string {
  if (!facturePath) return "";
  const storageBase = getStorageBaseUrl();
  let p = getUrlPathAndQuery(facturePath);
  p = p.replace(/^\/uploads\/factures\//, "/Images/docs/factures/");
  return `${storageBase}${p}`;
}

/**
 * Construit l'URL d'un fichier HiPay (infoClientHipay) directement depuis le storage.
 * /uploads/infoClientHipay/{id}/{file} -> {storage}/Images/docs/infoClientHipay/{id}/{file}
 */
export function getHipayFileUrl(hipayPath: string): string {
  if (!hipayPath) return "";
  const storageBase = getStorageBaseUrl();
  let p = getUrlPathAndQuery(hipayPath);
  p = p.replace(/^\/uploads\/infoClientHipay\//, "/Images/docs/infoClientHipay/");
  return `${storageBase}${p}`;
}

/**
 * Construit l'URL d'une proforma / pré-commande directement depuis le storage.
 * /uploads/proforma/{userId}/{file} -> {storage}/Images/docs/proforma/{userId}/{file}
 */
export function getProformaUrl(proformaPath: string): string {
  if (!proformaPath) return "";
  const storageBase = getStorageBaseUrl();
  let p = getUrlPathAndQuery(proformaPath);
  p = p.replace(/^\/uploads\/proforma\//, "/Images/docs/proforma/");
  return `${storageBase}${p}`;
}

/** Convertit un chemin ou une URL d'aperçu vers l'URL storage de la variante originale. */
export function getProductOriginalImageUrl(photoPathOrUrl: string): string {
  if (!photoPathOrUrl) return "";

  let rawPath = photoPathOrUrl;
  if (/^https?:\/\//i.test(photoPathOrUrl)) {
    try {
      rawPath = new URL(photoPathOrUrl).pathname;
    } catch {
      rawPath = (photoPathOrUrl.split("?")[0] || photoPathOrUrl).trim();
    }
  } else {
    rawPath = (photoPathOrUrl.split("?")[0] || photoPathOrUrl).trim();
  }

  const originalPath = rawPath
    .replace(/\/Produits\/(accueil|fiche|liste|zoom)\//i, "/Produits/original/")
    .replace(/\/(accueil|fiche|liste|zoom)\//i, "/original/");

  return getProductImageUrl(originalPath);
}

export function getGraphQLUrl(): string {
  const envUrl = process.env.VUE_APP_GRAPHQL_URL;
  if (envUrl) return envUrl;
  const base = getApiBaseUrl();
  if (base) return `${base}/graphql`;
  if (process.env.NODE_ENV === "development")
    return "http://localhost:3002/graphql";
  return "/graphql";
}

function getAuthFetchOptions(init?: RequestInit, authToken?: string | null): {
  headers: Record<string, string>;
  credentials: RequestCredentials;
} {
  const authStore = useAuthStore();
  const headers: Record<string, string> = {};

  const initHeaders = init?.headers;
  if (initHeaders instanceof Headers) {
    initHeaders.forEach((value, key) => {
      headers[key] = value;
    });
  } else if (Array.isArray(initHeaders)) {
    for (const [key, value] of initHeaders) {
      headers[key] = value;
    }
  } else if (initHeaders) {
    Object.assign(headers, initHeaders);
  }

  const token = authToken !== undefined ? authToken : authStore.token;
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const credentials = token
    ? ("omit" as RequestCredentials)
    : ((init?.credentials as RequestCredentials | undefined) ?? "include");

  return { headers, credentials };
}

function isPublicAuthPage(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  return (
    path === "/auth" ||
    path === "/register" ||
    path === "/reset-password" ||
    path.endsWith("/auth") ||
    path.endsWith("/register") ||
    path.endsWith("/reset-password")
  );
}

/** Déconnecte et nettoie la session (vendeur ou admin selon la zone). */
export function handleAuthFailure(): void {
  const authStore = useAuthStore();
  authStore.clearImpersonation?.();

  if (isPublicAuthPage()) {
    authStore.user = null;
    authStore.authenticated = false;
    authStore.token = null;
    localStorage.removeItem("user_mel");
    return;
  }

  const isAdminArea =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/adminEFP");

  if (isAdminArea) {
    void (async () => {
      try {
        const { useAdminEfpStore } = await import("@/stores/adminEfp");
        await useAdminEfpStore().logout();
      } catch {
        /* ignorer */
      } finally {
        authStore.user = null;
        authStore.authenticated = false;
        authStore.token = null;
        localStorage.removeItem("user_mel");
        const path = window.location.pathname.replace(/\/$/, "") || "/";
        if (!path.includes("/auth")) {
          window.location.href = "/adminEFP/auth";
        }
      }
    })();
    return;
  }

  void authStore.logout?.();
}

function throwAuthError(): never {
  throw new Error(AUTH_ERROR_MESSAGE);
}

async function assertRestAuthorized(
  response: Response,
  skipAuthRedirect: boolean,
): Promise<void> {
  if (skipAuthRedirect) return;

  const shouldLogout = async (): Promise<boolean> => {
    const authStore = useAuthStore();
    if (authStore.isImpersonating) return false;
    const { useAdminEfpStore } = await import("@/stores/adminEfp");
    return !useAdminEfpStore().isAdminEfpValidated;
  };

  if (response.status === 401 || response.status === 403) {
    if (await shouldLogout()) {
      handleAuthFailure();
      throwAuthError();
    }
    return;
  }

  if (!response.ok) {
    try {
      const cloned = response.clone();
      const contentType = cloned.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        const data = (await cloned.json()) as { message?: unknown };
        const message =
          typeof data.message === "string" ? data.message : "";
        if (message.includes("Unauthorized")) {
          if (await shouldLogout()) {
            handleAuthFailure();
            throwAuthError();
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message === AUTH_ERROR_MESSAGE) {
        throw error;
      }
    }
  }
}

export interface FetchRestOptions extends RequestInit {
  /** Routes publiques ou appels internes (logout, statut impersonation…). */
  skipAuthRedirect?: boolean;
  /** Token temporaire pour un endpoint REST spécialisé. */
  authToken?: string | null;
}

/** fetch REST avec cookie/token et déconnexion automatique si session invalide. */
export async function fetchRest(
  url: string,
  init?: FetchRestOptions,
): Promise<Response> {
  const { skipAuthRedirect = false, authToken, ...fetchInit } = init ?? {};
  const { headers, credentials } = getAuthFetchOptions(fetchInit, authToken);

  const response = await fetch(url, {
    ...fetchInit,
    headers,
    credentials,
  });

  await assertRestAuthorized(response, skipAuthRedirect);
  return response;
}

const GRAPHQL_URL = getGraphQLUrl();

export interface FetchApiOptions {
  skipAuthRedirect?: boolean;
}

export async function fetchApi<T = any>(
  query: string,
  variables?: Record<string, any>,
  options?: FetchApiOptions,
): Promise<T> {
  const skipAuthRedirect = options?.skipAuthRedirect ?? false;
  const controller = new AbortController();
  // const timeoutId  = setTimeout(() => controller.abort(), 10000);

  try {
    const { headers, credentials } = getAuthFetchOptions({
      headers: { "Content-Type": "application/json" },
    });

    const response = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({ query, variables }),
      credentials,
      signal: controller.signal,
    });

    // clearTimeout(timeoutId);

    if (!response.ok) {
      if (
        !skipAuthRedirect &&
        (response.status === 401 || response.status === 403)
      ) {
        const authStore = useAuthStore();
        const { useAdminEfpStore } = await import("@/stores/adminEfp");
        const adminEfp = useAdminEfpStore();

        if (
          !authStore.isImpersonating &&
          !adminEfp.isAdminEfpValidated
        ) {
          handleAuthFailure();
          throwAuthError();
        }
      }
      throw new Error(`HTTP ${response.status}`);
    }

    const result: GraphQLResponse<T> = await response.json();

    if (result.errors?.length) {
      if (
        !skipAuthRedirect &&
        result.errors[0].message.includes("Unauthorized")
      ) {
        const authStore = useAuthStore();
        const { useAdminEfpStore } = await import("@/stores/adminEfp");
        const adminEfp = useAdminEfpStore();

        if (
          !authStore.isImpersonating &&
          !adminEfp.isAdminEfpValidated
        ) {
          handleAuthFailure();
        }
      }
      throw new Error(result.errors[0].message);
    }

    if (!result.data) {
      throw new Error("Aucune donnée retournée");
    }

    return result.data;
  } catch (error) {
    // clearTimeout(timeoutId);

    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Délai dépassé");
    }

    throw error;
  }
}
